/**
 * Jotform Service
 *
 * Frontend service for interacting with Jotform through our edge functions.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { createServiceError } from '@/lib/apiErrors';
import type { FormProvider } from '@/types/canonical';

// Jotform-specific types
export interface JotformForm {
  id: string;
  name: string;
  modified: string;
  created_at: string;
  status: string;
  url: string;
  count: string;
  new: string;
  favorite: boolean;
  archived: boolean;
  type: string;
  _original: Record<string, unknown>;
  _provider: 'jotform';
}

export interface JotformBackup {
  id: string;
  user_id: string;
  provider: FormProvider;
  form_id_external: string;
  form_data_json: Record<string, unknown>;
  canonical_data?: Record<string, unknown> | null;
  kind?: 'manual' | 'auto' | null;
  content_hash?: string | null;
  expires_at?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface JotformUser {
  username: string;
  email: string;
  name: string;
  accountType: string;
}

export interface JotformAuthResult {
  success: boolean;
  message: string;
  user: JotformUser;
  limitLeft: number;
}

export interface JotformBackupResult {
  success: boolean;
  backup: {
    id: string;
    formId: string;
    formName: string;
    createdAt: string;
    questionsCount: number;
    webhooksCount: number;
  };
}

export interface JotformRestoreResult {
  success: boolean;
  newFormId: string;
  formName: string;
  formUrl: string;
  method: 'clone' | 'create';
}

interface ErrorPayload {
  error?: string;
  message?: string;
  code?: string;
  [key: string]: unknown;
}

/**
 * Check if user has Jotform credentials
 */
export const checkJotformConnection = async (): Promise<boolean | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return false;
  }

  const { data, error } = await supabase
    .from('user_provider_credentials')
    .select('id')
    .eq('user_id', user.id)
    .eq('provider', 'jotform')
    .maybeSingle();

  if (error) {
    logger.error("Error checking Jotform connection status:", error);
    return null;
  }

  return !!data;
};

/**
 * Validate and store a Jotform credential
 */
export const connectJotform = async (apiKey: string): Promise<JotformAuthResult> => {
  logger.debug("Attempting to validate Jotform credential...");

  const { data, error } = await supabase.functions.invoke('jotform-auth-validate', {
    method: 'POST',
    body: { apiKey },
  });

  if (error) {
    logger.error("Error invoking jotform-auth-validate:", error);
    const errorMessage = (data as ErrorPayload)?.error || error.message || "Failed to validate Jotform connection";
    throw new Error(errorMessage);
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  if (!data?.success) {
    throw new Error("Failed to connect to Jotform");
  }

  logger.debug("Jotform credential validated successfully");
  return data as JotformAuthResult;
};

/**
 * Disconnect Jotform (remove credentials)
 */
export const disconnectJotform = async (): Promise<void> => {
  const { error } = await supabase.functions.invoke('jotform-disconnect', {
    method: 'POST',
  });

  if (error) {
    logger.error("Error disconnecting Jotform:", error);
    throw new Error("Failed to disconnect Jotform");
  }

  logger.debug("Jotform disconnected successfully");
};

/**
 * Fetch all forms from Jotform
 */
export const fetchJotformForms = async (): Promise<JotformForm[]> => {
  logger.debug("Attempting to fetch Jotform forms...");

  const { data, error } = await supabase.functions.invoke('jotform-proxy');

  if (error) {
    logger.error("Error invoking jotform-proxy:", error);
    let responseData: ErrorPayload | null = null;

    try {
      const jsonFunc = (error as { context?: { json?: () => Promise<ErrorPayload> } })?.context?.json;
      if (typeof jsonFunc === 'function') {
        responseData = await jsonFunc();
      }
    } catch (e) {
      logger.error('[JOTFORM FETCH] Failed to parse error response:', e);
    }

    if (
      error.message?.includes("NOT_CONNECTED") ||
      error.message?.includes("REAUTH_REQUIRED") ||
      responseData?.code === "REAUTH_REQUIRED" ||
      (data as ErrorPayload)?.code === "REAUTH_REQUIRED"
    ) {
      throw new Error("REAUTH_REQUIRED");
    }

    const errorMessage = responseData?.error || (data as ErrorPayload)?.error || error.message || "Failed to fetch Jotform forms";
    throw createServiceError(errorMessage, error);
  }

  if (data?.error) {
    if (data.code === "REAUTH_REQUIRED") {
      throw new Error("REAUTH_REQUIRED");
    }
    throw new Error(data.error);
  }

  if (data?.forms) {
    logger.debug("Successfully fetched Jotform forms:", data.forms.length);
    return data.forms as JotformForm[];
  }

  logger.warn("jotform-proxy response did not contain forms array:", data);
  return [];
};

/**
 * Backup a Jotform form
 */
export const backupJotformForm = async (formId: string): Promise<JotformBackupResult> => {
  logger.debug(`Attempting to backup Jotform form: ${formId}`);

  const { data, error } = await supabase.functions.invoke('jotform-backup-form-proxy', {
    method: 'POST',
    body: { formId },
  });

  if (error) {
    logger.error(`Error invoking jotform-backup-form-proxy for form ${formId}:`, error);

    if ((data as ErrorPayload)?.code === "REAUTH_REQUIRED") {
      throw new Error("REAUTH_REQUIRED");
    }

    const errorMessage = (data as ErrorPayload)?.error || error.message || "Failed to backup form";
    throw createServiceError(errorMessage, error);
  }

  if (data?.error) {
    if (data.code === "REAUTH_REQUIRED") {
      throw new Error("REAUTH_REQUIRED");
    }
    throw new Error(data.error);
  }

  if (!data?.success) {
    throw new Error("Backup failed");
  }

  logger.debug(`Jotform form ${formId} backed up successfully`);
  return data as JotformBackupResult;
};

/**
 * Restore a Jotform form from backup
 */
export const restoreJotformForm = async (
  backupId: string,
  newFormName?: string
): Promise<JotformRestoreResult> => {
  logger.debug(`Attempting to restore Jotform form from backup: ${backupId}`);

  const { data, error } = await supabase.functions.invoke('jotform-restore-form-proxy', {
    method: 'POST',
    body: { backupId, newFormName },
  });

  if (error) {
    logger.error(`Error invoking jotform-restore-form-proxy for backup ${backupId}:`, error);

    if ((data as ErrorPayload)?.code === "REAUTH_REQUIRED") {
      throw new Error("REAUTH_REQUIRED");
    }

    const errorMessage = (data as ErrorPayload)?.error || error.message || "Failed to restore form";
    throw createServiceError(errorMessage, error);
  }

  if (data?.error) {
    if (data.code === "REAUTH_REQUIRED") {
      throw new Error("REAUTH_REQUIRED");
    }
    throw new Error(data.error);
  }

  if (!data?.success) {
    throw new Error("Restore failed");
  }

  logger.debug(`Jotform form restored successfully, new form ID: ${data.newFormId}`);
  return data as JotformRestoreResult;
};

/**
 * Fetch backups for a specific Jotform form
 */
export const fetchJotformBackups = async (formIdExternal?: string): Promise<JotformBackup[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("User not authenticated");
  }

  let query = supabase
    .from('form_backups')
    .select('*')
    .eq('user_id', user.id)
    .eq('provider', 'jotform')
    .order('created_at', { ascending: false });

  if (formIdExternal) {
    query = query.eq('form_id_external', formIdExternal);
  }

  const { data, error } = await query;

  if (error) {
    logger.error("Error fetching Jotform backups:", error);
    throw new Error("Failed to fetch backups");
  }

  return (data || []) as JotformBackup[];
};

/**
 * Fetch all Jotform backups for the user
 */
export const fetchAllJotformBackups = async (): Promise<JotformBackup[]> => {
  return fetchJotformBackups();
};

/**
 * Delete a Jotform backup
 */
export const deleteJotformBackup = async (backupId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("User not authenticated");
  }

  const { error } = await supabase
    .from('form_backups')
    .delete()
    .eq('id', backupId)
    .eq('user_id', user.id)
    .eq('provider', 'jotform');

  if (error) {
    logger.error("Error deleting Jotform backup:", error);
    throw new Error("Failed to delete backup");
  }

  logger.debug(`Jotform backup ${backupId} deleted successfully`);
};
